import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ChevronRightIcon,
  NewDonutChart,
  Preloader,
  Responsive,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { RiskControlOptions } from '@/common/constants';
import { useInvestmentsHoldings, useInvestor, useRiskModels } from '@/modules/investments/hooks';
import { InvestmentPaths } from '@/modules/investments/investments.types';
import InvestmentSwitchModel from '@/modules/investments/InvestmentSwitchModal';

import { useGetInvestorAccountId, useInvestmentsProcessingSnapshot } from '../../hook';

import InvestmentDonutGroups from './InvestmentDonutGroups';
import InvestmentDonutMobileModal from './InvestmentDonutMobileModal';

interface Props {
  isExpandedMode?: boolean;
  isModalMode?: boolean;
  isPerformanceMode?: boolean;
}
const InvestmentDonut: React.FC<Props> = ({ isExpandedMode, isModalMode, isPerformanceMode }) => {
  const { isMobile } = useResponsive();
  const accountId = useGetInvestorAccountId();
  const {
    data: { currentToleranceModel, type: investmentType },
  } = useInvestor(accountId);
  const { isHoldingLoading, formattedByCategory, formattedByCategoryCut, isSuccessLoaded } =
    useInvestmentsHoldings(accountId);
  const { t } = useTranslation();
  const { currentShortOption } = useRiskModels(currentToleranceModel as RiskControlOptions);

  const titleNode = useMemo(() => {
    if (isMobile) {
      return (
        <>
          {investmentType === InvestmentPaths.MANAGED ? (
            <Box
              direction="column"
              align="center"
              justify="center"
              width={!isModalMode ? undefined : { max: '160px' }}
            >
              <Text weight={700} truncate color="textBody" size={isModalMode ? 'large' : 'medium'}>
                {t('Managed')}
              </Text>
              <Text
                textAlign={!isModalMode ? 'start' : 'center'}
                color="textDisabled"
                size={isModalMode ? 'medium' : 'small'}
                truncate={!isModalMode}
                title={currentShortOption?.label || ''}
                weight={isModalMode ? 400 : 500}
              >
                {currentShortOption?.label}
              </Text>
            </Box>
          ) : investmentType === InvestmentPaths.SELF_DIRECTED ? (
            <Text
              weight={700}
              textAlign={!isModalMode ? 'start' : 'center'}
              truncate
              color="textBody"
              size={isModalMode ? 'large' : 'medium'}
            >
              {t('Self Directed')}
            </Text>
          ) : null}
        </>
      );
    }
    return (
      <Box width={{ max: '90px' }} align="center">
        {investmentType === InvestmentPaths.MANAGED ? (
          <>
            <Text weight={700} color="textBody" size="medium">
              {t('Managed')}
            </Text>
            <Text
              textAlign="center"
              color="textDisabled"
              size="small"
              title={currentShortOption?.label || ''}
              weight={500}
            >
              {currentShortOption?.label}
            </Text>
          </>
        ) : investmentType === InvestmentPaths.SELF_DIRECTED ? (
          <Box width={{ max: '88px' }}>
            <Text weight={700} textAlign="center" color="textBody" size="medium">
              {t('Self Directed')}
            </Text>
          </Box>
        ) : null}
      </Box>
    );
  }, [currentShortOption?.label, investmentType, isMobile, isModalMode, t]);
  const isSnapshotShowed = useInvestmentsProcessingSnapshot();
  const donutNode = useMemo(() => {
    if (!formattedByCategoryCut.length || isSnapshotShowed) {
      return (
        <Box direction="row" align="center" justify="center">
          <Box
            round="50%"
            direction="row"
            align="center"
            justify="center"
            width={isMobile ? '56px' : '152px'}
            height={isMobile ? '56px' : '152px'}
            background="border1"
          >
            <Box
              direction="row"
              align="center"
              round="50%"
              justify="center"
              width={isMobile ? '48px' : '112px'}
              height={isMobile ? '48px' : '112px'}
              background="canvas"
            >
              {isMobile ? null : titleNode}
            </Box>
          </Box>
        </Box>
      );
    }
    return (
      <NewDonutChart
        gap={0.005}
        strokeWidth={isMobile ? 12 : 19}
        testId="investment"
        data={formattedByCategoryCut}
        size={isMobile ? 85 : 166}
      >
        {isMobile ? null : titleNode}
      </NewDonutChart>
    );
  }, [formattedByCategoryCut, isMobile, isSnapshotShowed, titleNode]);
  if (isModalMode) {
    return (
      <Box direction="column" pad="spacing16">
        <div>
          <NewDonutChart
            gap={0.01}
            strokeWidth={29}
            isOnClickHintMode
            testId="investment"
            data={formattedByCategoryCut}
            size={220}
          >
            {titleNode}
          </NewDonutChart>
        </div>
        <div>
          <InvestmentDonutGroups isModalMode value={formattedByCategory} />
        </div>
      </Box>
    );
  }
  return (
    <Box
      fill
      direction={isMobile ? 'row' : 'column'}
      round="moduleRound"
      width={{ min: isMobile ? undefined : '200px' }}
      background="canvas"
      pad={
        isMobile
          ? {
              left: 'spacing8',
              top: 'spacing12',
              bottom: 'spacing12',
              right: 'spacing12',
            }
          : undefined
      }
      height={{ min: isMobile ? '112px' : undefined }}
      data-testid="investment-donut"
      style={isMobile ? { flex: 1 } : {}}
    >
      <Responsive sizes={[Responsive.Tablet, Responsive.Desktop]}>
        <Box
          align="center"
          justify="center"
          width={{ min: '200px' }}
          height="200px"
          style={{ position: 'relative' }}
        >
          <Box style={{ position: 'absolute', right: 4, top: 4, zIndex: 100 }}>
            <InvestmentSwitchModel isIconMode accountId={accountId} />
          </Box>
          {isHoldingLoading && !isSuccessLoaded ? <Preloader size={50} /> : donutNode}
        </Box>
      </Responsive>
      <Responsive size={Responsive.Mobile}>
        <InvestmentDonutMobileModal switchNode={<InvestmentSwitchModel isIconMode accountId={accountId} />}>
          <Box direction="row" align="center">
            <div>
              <Box width="85px">{donutNode}</Box>
            </div>
            <Box fill justify="center" align="start" margin={{ left: 'spacing8' }}>
              {titleNode}
            </Box>
            {isPerformanceMode ? (
              <Box align="center" justify="center" width={{ min: '40px' }} height="40px">
                <ChevronRightIcon size="16px" color="iconPrimary" />
              </Box>
            ) : (
              <Box align="center" justify="center" width={{ min: '24px' }} height="24px">
                <ChevronRightIcon size="12px" color="iconSecondary" />
              </Box>
            )}

            {/* <div>
            <InvestmentSwitchModel isIconMode accountId={accountId} isMobileMode />
          </div> */}
          </Box>
        </InvestmentDonutMobileModal>
      </Responsive>

      {(isExpandedMode || isModalMode) && <InvestmentDonutGroups value={formattedByCategory} />}
    </Box>
  );
};

export default InvestmentDonut;
